/**
 * How routines read (slice 5 spec §11): when one runs, its next run, its
 * last run's state, a run's lines on Routine detail, the schedule as a
 * sentence, and the computer time. Pure, so the pages share it and it
 * tests without rendering. Times are the workspace's (`tz`).
 */
import {
  checkSchedule,
  localDate,
  nextRun,
  REMINDER_MAX_AHEAD_MS,
  REMINDER_MIN_AHEAD_MS,
  zonedTime,
} from "../../../worker/domain/routine";
import type { RoutineDetail, RoutineItem, RoutineRun, RoutineWeek, RoutineWhen } from "./api";

// ── Times and days ───────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");

/** "07:00" → "7:00 am", "18:30" → "6:30 pm". */
export function timeWords(time: string): string {
  const [h, m] = time.split(":").map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${pad(m)} ${h < 12 ? "am" : "pm"}`;
}

/** A window's end, short: "08:00" → "8 am", "17:30" → "5:30 pm". */
function hourWords(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${m === 0 ? hour : `${hour}:${pad(m)}`} ${h < 12 ? "am" : "pm"}`;
}

/** An instant's local time: "7:01 am". */
function clockAt(at: string | Date, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(at));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return timeWords(`${pad(h % 24)}:${pad(m)}`);
}

const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_SHORT = "Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec".split(" ");
const MONTH_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** The weekday and day of a `YYYY-MM-DD`. */
function dateParts(date: string) {
  const d = new Date(`${date}T12:00:00Z`);
  return { weekday: d.getUTCDay(), day: d.getUTCDate(), month: d.getUTCMonth() };
}

/** "Mon 13 Oct". */
function shortDate(date: string): string {
  const p = dateParts(date);
  return `${DAY_SHORT[p.weekday]} ${p.day} ${MONTH_SHORT[p.month]}`;
}

/** "Monday 13 October". */
function longDate(date: string): string {
  const p = dateParts(date);
  return `${DAY_LONG[p.weekday]} ${p.day} ${MONTH_LONG[p.month]}`;
}

/** The local date `days` after `date`, by the calendar (a clock change's day isn't 24 hours). */
function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** "today", "tomorrow", or null for any other day. */
function nearDay(date: string, today: string): "today" | "tomorrow" | null {
  if (date === today) return "today";
  return date === addDays(today, 1) ? "tomorrow" : null;
}

/** "Today", "Tomorrow", "Mon 13 Oct". */
function dayRef(date: string, today: string): string {
  const near = nearDay(date, today);
  return near ? near[0].toUpperCase() + near.slice(1) : shortDate(date);
}

const WEEKDAYS = [1, 2, 3, 4, 5] as const;
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6] as const;

const sameDays = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((d) => b.includes(d));

/** Monday first, as the day buttons are. */
const mondayFirst = (days: readonly number[]) =>
  [...days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7));

/** A row's days: "Weekdays", "Every day", "Mondays", "Mon, Wed". */
export function daysWords(days: readonly number[]): string {
  if (sameDays(days, ALL_DAYS)) return "Every day";
  if (sameDays(days, WEEKDAYS)) return "Weekdays";
  if (days.length === 1) return `${DAY_LONG[days[0]]}s`;
  return mondayFirst(days)
    .map((d) => DAY_SHORT[d])
    .join(", ");
}

/** A sentence's days: "every weekday", "every day", "every Monday", "Monday and Wednesday". */
function daysSentence(days: readonly number[]): string {
  if (sameDays(days, ALL_DAYS)) return "every day";
  if (sameDays(days, WEEKDAYS)) return "every weekday";
  if (days.length === 1) return `every ${DAY_LONG[days[0]]}`;
  const names = mondayFirst(days).map((d) => DAY_LONG[d]);
  return `every ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** "on weekdays", "every day", "on Monday and Wednesday". */
function daysOn(days: readonly number[]): string {
  if (sameDays(days, ALL_DAYS)) return "every day";
  if (sameDays(days, WEEKDAYS)) return "on weekdays";
  const names = mondayFirst(days).map((d) => DAY_LONG[d]);
  if (names.length === 1) return `on ${names[0]}s`;
  return `on ${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const sentence = (s: string) => s[0].toUpperCase() + s.slice(1);

const everyWords = (every: number) => (every === 1 ? "Every hour" : `Every ${every} hours`);

// ── A row on Routines (spec §11.1) ───────────────────────────────────────

/**
 * When: "Weekdays 7:00 am", "Every 2 hours, 8 am–6 pm" (with its days
 * unless they're weekdays), "Today, 6:30 pm".
 */
export function whenLabel(r: RoutineWhen, tz: string, now: Date): string {
  if (r.kind === "once") {
    return `${dayRef(r.on ?? "", localDate(now, tz))}, ${timeWords(r.time ?? "00:00")}`;
  }
  if (r.every && r.from && r.to) {
    const window = `${everyWords(r.every)}, ${hourWords(r.from)}–${hourWords(r.to)}`;
    return sameDays(r.days, WEEKDAYS) ? window : `${window} · ${daysWords(r.days)}`;
  }
  return `${daysWords(r.days)} ${timeWords(r.time ?? "00:00")}`;
}

/** "In 2 hours", "In 25 min", "In 4 days". */
function inTime(at: string, now: Date): string {
  const min = Math.max(1, Math.round((new Date(at).getTime() - now.getTime()) / 60_000));
  if (min < 60) return `In ${min} min`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `In ${hours} ${hours === 1 ? "hour" : "hours"}`;
  const days = Math.round(hours / 24);
  return `In ${days} ${days === 1 ? "day" : "days"}`;
}

/** The person's first name, or "you" for the viewer. */
function whose(person: RoutineItem["for"], viewerId: string): { name: string; you: boolean } {
  if (!person) return { name: "its person", you: false };
  if (person.id === viewerId) return { name: "you", you: true };
  return { name: person.name.split(/\s+/)[0] ?? person.name, you: false };
}

/**
 * Next, under When: "Next: tomorrow", "Next: today, 2:00 pm", "Next: Fri 10
 * Oct"; a reminder's "In 2 hours"; "Back on at Ana's next visit" when
 * paused; "Off"; "Done" once a reminder has run.
 */
export function nextLabel(
  r: Pick<
    RoutineItem,
    "kind" | "every" | "enabled" | "paused_idle" | "next_run_at" | "ended_at" | "for"
  > & { last_run?: RoutineItem["last_run"] },
  viewerId: string,
  tz: string,
  now: Date,
): string {
  const today = localDate(now, tz);
  if (r.ended_at) return "Done";
  if (r.paused_idle) {
    const who = whose(r.for, viewerId);
    return who.you ? "Back on at your next visit" : `Back on at ${who.name}'s next visit`;
  }
  if (!r.enabled) return "Off";
  if (!r.next_run_at) return "Not scheduled";
  if (r.kind === "once") return inTime(r.next_run_at, now);
  const date = localDate(new Date(r.next_run_at), tz);
  const near = nearDay(date, today);
  const time = clockAt(r.next_run_at, tz);
  if (near === "today") return `Next: today, ${time}`;
  // At a time, tomorrow's time is the routine's own; by the hour it's the first slot.
  if (near === "tomorrow") return r.every ? `Next: tomorrow, ${time}` : "Next: tomorrow";
  return `Next: ${shortDate(date)}`;
}

export type LastRunIcon = "check" | "pause" | "dash" | "alert" | "phone" | "screen" | "running";
export type LastRunTone = "moss" | "muted" | "copper" | "destructive";
export type LastRunView = { text: string; icon: LastRunIcon | null; tone: LastRunTone };

/** Each tone's text colour. */
export const TONE_TEXT: Record<LastRunTone, string> = {
  moss: "text-moss",
  muted: "text-muted-foreground",
  copper: "text-copper",
  destructive: "text-destructive",
};

/** "7:01 am" for today's, "Wed 8 Oct" for an older one. */
function whenRan(at: string, tz: string, now: Date): string {
  const date = localDate(new Date(at), tz);
  return date === localDate(now, tz) ? clockAt(at, tz) : shortDate(date);
}

const SKIPPED_ROW: Record<string, string> = {
  previous_running: "Skipped · last run still going",
  ai_not_connected: "Skipped · the AI wasn't connected",
};

/**
 * The last-run lane (spec §11.1), an icon and colour each: "Texted to you ·
 * 7:01 am" (moss), "Quiet day, not texted · 7:00 am" (muted), "Paused · not
 * seen for 14 days" (copper), "Skipped · last run still going" (copper),
 * "Failed · Retry in its chat" (destructive). A reminder still to come says
 * how it will arrive: "Will text you", or "In the app · no phone linked".
 */
export function lastRunView(r: RoutineItem, viewerId: string, tz: string, now: Date): LastRunView {
  if (r.paused_idle) {
    return { text: "Paused · not seen for 14 days", icon: "pause", tone: "copper" };
  }
  if (r.kind === "once" && !r.ended_at) return arrivalView(r, viewerId);
  const last = r.last_run;
  if (!last) return { text: "Not run yet", icon: null, tone: "muted" };
  const at = whenRan(last.at, tz, now);
  switch (last.status) {
    case "running":
      return { text: "Running now", icon: "running", tone: "moss" };
    case "error":
      return { text: "Failed · Retry in its chat", icon: "alert", tone: "destructive" };
    case "skipped":
      return {
        text:
          last.skip_reason === "missed"
            ? `Missed · Crew was down · ${at}`
            : (SKIPPED_ROW[last.skip_reason ?? ""] ?? "Skipped"),
        icon: "dash",
        tone: "copper",
      };
    case "done":
      return doneView(r, last.texted, whose(r.for, viewerId).name, at);
  }
}

/** A reminder still to come: "Will text you", or "In the app · no phone linked". */
function arrivalView(r: RoutineItem, viewerId: string): LastRunView {
  if (r.text_me) {
    return { text: `Will text ${whose(r.for, viewerId).name}`, icon: "phone", tone: "muted" };
  }
  const noPhone = r.for && !r.for.phone_linked;
  return {
    text: noPhone ? "In the app · no phone linked" : "In the app",
    icon: "screen",
    tone: "muted",
  };
}

/** A done run: texted (moss), a quiet day with Text it to me on, or in the app. */
function doneView(r: RoutineItem, texted: boolean, who: string, at: string): LastRunView {
  if (texted) return { text: `Texted to ${who} · ${at}`, icon: "check", tone: "moss" };
  if (r.text_me && r.kind === "repeat") {
    return { text: `Quiet day, not texted · ${at}`, icon: "check", tone: "muted" };
  }
  return { text: `In the app · ${at}`, icon: "check", tone: "muted" };
}

/**
 * Whether the viewer has the On switch (spec §11.1): their own, and the
 * owner's on anyone's, which can only turn it off.
 */
export function switchState(
  r: Pick<RoutineItem, "enabled" | "paused_idle" | "for" | "ended_at">,
  viewer: { id: string; role: "owner" | "member" },
): { checked: boolean; disabled: boolean } {
  const checked = r.enabled && !r.paused_idle;
  if (r.ended_at) return { checked: false, disabled: true };
  if (r.for?.id === viewer.id) return { checked, disabled: false };
  return { checked, disabled: !(viewer.role === "owner" && checked) };
}

/** A row reads muted while it won't run: off, or paused for no visits. */
export const isMuted = (r: Pick<RoutineItem, "enabled" | "paused_idle">) =>
  !r.enabled || r.paused_idle;

export type RoutineSections = {
  routines: RoutineItem[];
  reminders: RoutineItem[];
  done: RoutineItem[];
};

/** Routines (repeating), Reminders (still to come) and Done this week (ended reminders). */
export function sections(items: readonly RoutineItem[]): RoutineSections {
  return {
    routines: items.filter((r) => r.kind === "repeat"),
    reminders: items.filter((r) => r.kind === "once" && !r.ended_at),
    done: items.filter((r) => r.kind === "once" && r.ended_at),
  };
}

/** "about 35 min", "under a minute": computer time from its milliseconds. */
function aboutMinutes(ms: number): string {
  if (ms < 60_000) return "under a minute";
  return `about ${Math.round(ms / 60_000)} min`;
}

/** The footer: "7 routines and reminders · about 35 min of computer time a week". */
export function footerLine(count: number, week: RoutineWeek): string {
  const n = `${count} ${count === 1 ? "routine or reminder" : "routines and reminders"}`;
  return `${n} · ${aboutMinutes(week.computer_ms)} of computer time a week`;
}

/** Routine detail's: "About 6 min of computer time a week". */
export function weekLine(week: RoutineWeek): string {
  return `${sentence(aboutMinutes(week.computer_ms))} of computer time a week`;
}

// ── Routine detail (spec §11.2) ──────────────────────────────────────────

/**
 * The schedule as a sentence: "Every weekday at 7:00 am", "Every 2 hours
 * from 8:00 am to 6:00 pm, on weekdays", "Once, Thursday 9 October at 6:30 pm".
 */
export function scheduleSentence(r: RoutineWhen): string {
  if (r.kind === "once") {
    return `Once, ${longDate(r.on ?? "")} at ${timeWords(r.time ?? "00:00")}`;
  }
  if (r.every && r.from && r.to) {
    const window = `${everyWords(r.every)} from ${timeWords(r.from)} to ${timeWords(r.to)}`;
    return `${window}, ${daysOn(r.days)}`;
  }
  return `${sentence(daysSentence(r.days))} at ${timeWords(r.time ?? "00:00")}`;
}

/** The header's state: "Next run tomorrow at 7:00 am", "Paused for no visits", "Off", "Ended". */
export function headerNext(
  r: Pick<RoutineItem, "enabled" | "paused_idle" | "next_run_at" | "ended_at">,
  tz: string,
  now: Date,
): string {
  if (r.ended_at) return "Ended";
  if (r.paused_idle) return "Paused for no visits";
  if (!r.enabled) return "Off";
  if (!r.next_run_at) return "Not scheduled";
  const date = localDate(new Date(r.next_run_at), tz);
  const day = nearDay(date, localDate(now, tz)) ?? shortDate(date);
  return `Next run ${day} at ${clockAt(r.next_run_at, tz)}`;
}

/** The phone's header line: "Next: tomorrow", "Paused", "Off", "Ended". */
export function shortNext(
  r: Pick<RoutineItem, "enabled" | "paused_idle" | "next_run_at" | "ended_at" | "kind" | "every">,
  tz: string,
  now: Date,
): string {
  if (r.ended_at) return "Ended";
  if (r.paused_idle) return "Paused";
  if (!r.enabled) return "Off";
  return nextLabel({ ...r, for: null }, "", tz, now);
}

/** The next run's line in Runs: "Tomorrow, 7:00 am". */
export function nextRunTitle(nextRunAt: string, tz: string, now: Date): string {
  const date = localDate(new Date(nextRunAt), tz);
  return `${dayRef(date, localDate(now, tz))}, ${clockAt(nextRunAt, tz)}`;
}

/** "1 min", "52 s". */
function runDuration(ms: number | null): string | null {
  if (ms === null) return null;
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} s`;
  return `${Math.round(ms / 60_000)} min`;
}

const SKIPPED_RUN: Record<string, string> = {
  previous_running: "Skipped: the last run was still going",
  ai_not_connected: "Skipped: the AI wasn't connected",
};

const FAILED_RUN: Record<string, string> = {
  couldnt_start: "Failed: the computer didn't wake in time",
  interrupted: "Failed: interrupted by a restart",
  stopped: "Failed: stopped partway",
  ai_auth: "Failed: the AI signed out",
};

export type RunTone = "moss" | "muted" | "copper" | "destructive";

export type RunView = {
  title: string;
  detail: string;
  detailTone: RunTone;
  icon: "check" | "dash" | "alert" | "running";
  /** "Texted" (moss) or "Quiet" on a done run. */
  aside: { text: string; tone: RunTone } | null;
};

/** The reply's first line, as the list cuts it. */
function firstLine(snippet: string | null): string | null {
  const line = snippet
    ?.split("\n")
    .find((l) => l.trim())
    ?.trim();
  return line ? line.replace(/^[#>*\-\s]+/, "").replace(/[.:]$/, "") : null;
}

/**
 * A run in Runs (spec §11.2): its date (a skipped one's day only), "· Run
 * now" on a manual one, its first line and duration, or why it didn't run.
 */
export function runView(
  run: RoutineRun,
  routine: Pick<RoutineItem, "kind" | "text_me">,
  tz: string,
  now: Date,
): RunView {
  const date = localDate(new Date(run.scheduled_for), tz);
  const day = dayRef(date, localDate(now, tz));
  const manual = run.manual ? " · Run now" : "";
  const title =
    run.status === "skipped"
      ? `${day}${manual}`
      : `${day}, ${clockAt(run.scheduled_for, tz)}${manual}`;
  switch (run.status) {
    case "running":
      return { title, detail: "Running now", detailTone: "muted", icon: "running", aside: null };
    case "skipped":
      return {
        title,
        detail:
          run.skip_reason === "missed"
            ? `Missed: Crew was down at ${clockAt(run.scheduled_for, tz)}`
            : (SKIPPED_RUN[run.skip_reason ?? ""] ?? "Skipped"),
        detailTone: "copper",
        icon: "dash",
        aside: null,
      };
    case "error":
      return {
        title,
        detail: FAILED_RUN[run.fail_reason ?? ""] ?? "Failed",
        detailTone: "destructive",
        icon: "alert",
        aside: null,
      };
    case "done": {
      const parts = [firstLine(run.snippet) ?? "Done", runDuration(run.duration_ms)];
      const aside = run.texted
        ? { text: "Texted", tone: "moss" as const }
        : routine.kind === "repeat" && routine.text_me
          ? { text: "Quiet", tone: "muted" as const }
          : null;
      return {
        title,
        detail: parts.filter(Boolean).join(" · "),
        detailTone: "muted",
        icon: "check",
        aside,
      };
    }
  }
}

/**
 * Results go to's text line (spec §8, §11.2), with Text it to me on and a
 * linked phone: "Texted to you as its instructions say" (every morning, or
 * only when something needs you: the agent follows the words), a reminder's
 * "Texted to you when it runs".
 */
export function textedLine(
  r: Pick<RoutineDetail["routine"], "text_me" | "for" | "kind">,
  viewerId: string,
): string | null {
  if (!r.text_me || !r.for?.phone_linked) return null;
  const who = whose(r.for, viewerId);
  if (r.kind === "once") return `Texted to ${who.name} when it runs`;
  return `Texted to ${who.name} as its instructions say`;
}

/** "Changed by Nadia · 2 Oct", or "Set up by Crew at signup · not changed". */
export function changedLine(
  r: Pick<RoutineDetail["routine"], "changed_by" | "changed_at" | "created_via" | "seeded">,
  viewerId: string,
  tz: string,
  now: Date,
): string | null {
  if (r.changed_by && r.changed_at) {
    const who = r.changed_by.id === viewerId ? "you" : r.changed_by.name.split(/\s+/)[0];
    const date = localDate(new Date(r.changed_at), tz);
    return `Changed by ${who} · ${dayRef(date, localDate(now, tz)).toLowerCase()}`;
  }
  if (r.created_via === "signup") return "Set up by Crew at signup · not changed";
  if (r.created_via === "join") return "Set up by Crew when you joined · not changed";
  if (r.created_via === "backfill") return "Set up by Crew · not changed";
  if (r.created_via === "agent") return "Made in a chat · not changed";
  return null;
}

// ── New routine (spec §11.3) ─────────────────────────────────────────────

export type Repeats = "once" | "daily" | "weekdays" | "weekly" | "hourly1" | "hourly2" | "hourly4";

/** The Repeats menu, in its three groups (spec §11.3). */
export const REPEATS_GROUPS: ReadonlyArray<ReadonlyArray<{ value: Repeats; label: string }>> = [
  [{ value: "once", label: "Once" }],
  [
    { value: "daily", label: "Every day" },
    { value: "weekdays", label: "Weekdays" },
    { value: "weekly", label: "Weekly" },
  ],
  [
    { value: "hourly1", label: "Every hour" },
    { value: "hourly2", label: "Every 2 hours" },
    { value: "hourly4", label: "Every 4 hours" },
  ],
];

export const isHourly = (r: Repeats): r is "hourly1" | "hourly2" | "hourly4" =>
  r.startsWith("hourly");

const everyOf = (r: Repeats) => (r === "hourly1" ? 1 : r === "hourly2" ? 2 : 4);

/** The preset the stored days match (spec §2.2): Every day, Weekdays, or Weekly. */
function presetOf(days: readonly number[]): "daily" | "weekdays" | "weekly" {
  if (sameDays(days, ALL_DAYS)) return "daily";
  if (sameDays(days, WEEKDAYS)) return "weekdays";
  return "weekly";
}

/** The form's fields. Times `HH:MM`, the date `YYYY-MM-DD`. */
export type RoutineForm = {
  title: string;
  instructions: string;
  repeats: Repeats;
  days: number[];
  time: string;
  on: string;
  from: string;
  to: string;
  /** "" for the workspace's default. */
  model: string;
  textMe: boolean;
  /** Whether the person set Text it to me; until then it follows the server's default. */
  textMeSet: boolean;
};

/** A new routine's form (board 82): Weekly, Mondays at 8:00 am; a reminder this evening. */
export function blankForm(tz: string, now: Date): RoutineForm {
  const today = localDate(now, tz);
  const evening = zonedTime(today, "18:00", tz).getTime() - now.getTime() > 5 * 60_000;
  return {
    title: "",
    instructions: "",
    repeats: "weekly",
    days: [1],
    time: "08:00",
    on: evening ? today : addDays(today, 1),
    from: "08:00",
    to: "18:00",
    model: "",
    textMe: false,
    textMeSet: false,
  };
}

/** Edit routine's form, filled in from the routine (board 81c). */
export function formOf(
  r: RoutineWhen & { title: string; instructions: string; model: string | null; text_me: boolean },
  tz: string,
  now: Date,
): RoutineForm {
  const blank = blankForm(tz, now);
  const repeats: Repeats =
    r.kind === "once" ? "once" : r.every ? (`hourly${r.every}` as Repeats) : presetOf(r.days);
  return {
    ...blank,
    title: r.title,
    instructions: r.instructions,
    repeats,
    days: r.kind === "once" ? WEEKDAYS.slice() : [...r.days],
    time: r.time ?? blank.time,
    on: r.on ?? blank.on,
    from: r.from ?? blank.from,
    to: r.to ?? blank.to,
    model: r.model ?? "",
    textMe: r.text_me,
    textMeSet: true,
  };
}

/**
 * A new Repeats choice: a preset fills in its days (spec §2.2); Weekly keeps
 * the days unless they're a preset's, then starts from one day. Coming from
 * Once, the hourly choices start on weekdays.
 */
export function withRepeats(form: RoutineForm, repeats: Repeats): RoutineForm {
  const days =
    repeats === "daily"
      ? [...ALL_DAYS]
      : repeats === "weekdays"
        ? [...WEEKDAYS]
        : repeats === "weekly"
          ? presetOf(form.days) === "weekly" && form.days.length > 0
            ? form.days
            : [1]
          : form.repeats === "once"
            ? [...WEEKDAYS]
            : form.days;
  return { ...form, repeats, days };
}

/** A day button pressed: the days change, and the menu shows the preset they match. */
export function withDay(form: RoutineForm, day: number): RoutineForm {
  const days = form.days.includes(day) ? form.days.filter((d) => d !== day) : [...form.days, day];
  const sorted = [...days].sort((a, b) => a - b);
  return {
    ...form,
    days: sorted,
    repeats: isHourly(form.repeats) ? form.repeats : presetOf(sorted),
  };
}

/**
 * Text it to me as the form shows it: the person's choice, else the
 * server's default (spec §6.1): on for a reminder with a linked phone, off
 * for a repeating routine.
 */
export function textMeShown(form: RoutineForm, phoneLinked: boolean): boolean {
  if (form.textMeSet) return form.textMe;
  return phoneLinked && form.repeats === "once";
}

/** The schedule fields the API takes (spec §10): exactly one shape. */
export function scheduleInput(form: RoutineForm) {
  if (form.repeats === "once") return { on: form.on, time: form.time };
  if (isHourly(form.repeats)) {
    return { days: form.days, every: everyOf(form.repeats), from: form.from, to: form.to };
  }
  return { days: form.days, time: form.time };
}

/** The hourly slots' hours, said: "8, 10, 12, 2, 4 and 6". */
function slotHours(from: string, to: string, every: number): string | null {
  const [fh, fm] = from.split(":").map(Number);
  const [th, tm] = to.split(":").map(Number);
  const start = fh * 60 + fm;
  const end = th * 60 + tm;
  const hours: string[] = [];
  for (let m = start; m <= end; m += every * 60) {
    const h = Math.floor(m / 60) % 24;
    const label = `${h % 12 === 0 ? 12 : h % 12}`;
    hours.push(m % 60 === 0 ? label : `${label}:${pad(m % 60)}`);
  }
  if (hours.length === 0) return null;
  if (hours.length === 1) return hours[0];
  return `${hours.slice(0, -1).join(", ")} and ${hours[hours.length - 1]}`;
}

export type Summary = { ok: true; text: string } | { ok: false; text: string };

/**
 * The summary, last in the When panel (spec §11.3): the schedule said back,
 * and its first (or, editing, next) run. "Every Monday at 8:00 am. First run
 * Monday 13 October (America/Chicago)." By the hour it lists the runs. A
 * schedule that can't be saved says why.
 */
export function summary(form: RoutineForm, tz: string, now: Date, editing: boolean): Summary {
  if (form.repeats === "once") return onceSummary(form, tz, now);
  if (form.days.length === 0) return { ok: false, text: "Pick at least one day." };
  const hourly = isHourly(form.repeats);
  if (!hourly && !form.time) return { ok: false, text: "Pick a time." };
  if (hourly && !(form.from && form.to && form.from < form.to)) {
    return { ok: false, text: "Between: the start needs to be before the end." };
  }
  const first = firstRun(form, tz, now, editing ? "Next run" : "First run", hourly);
  if (isHourly(form.repeats)) {
    const every = everyOf(form.repeats);
    const head = `${everyWords(every)} ${daysOn(form.days)}: ${slotHours(form.from, form.to, every)}.`;
    return { ok: true, text: `${head}${first} (${tz}).` };
  }
  const head = `${sentence(daysSentence(form.days))} at ${timeWords(form.time)}.`;
  return { ok: true, text: `${head}${first} (${tz}).` };
}

/** A reminder's summary: "Once, today at 6:30 pm (America/Chicago).", 2 minutes to a year ahead. */
function onceSummary(form: RoutineForm, tz: string, now: Date): Summary {
  if (!form.on || !form.time) return { ok: false, text: "Pick a date and a time." };
  const ahead = zonedTime(form.on, form.time, tz).getTime() - now.getTime();
  // The server's own limits (worker/domain/routine.ts).
  if (ahead < REMINDER_MIN_AHEAD_MS) {
    return { ok: false, text: "A reminder needs a time at least 2 minutes from now." };
  }
  if (ahead > REMINDER_MAX_AHEAD_MS)
    return { ok: false, text: "A reminder needs a date within a year." };
  const day = nearDay(form.on, localDate(now, tz)) ?? longDate(form.on);
  return { ok: true, text: `Once, ${day} at ${timeWords(form.time)} (${tz}).` };
}

/**
 * " First run Monday 13 October", by the hour with its time. The server's own
 * rules (worker/domain/routine.ts), so it's the run the server will write.
 */
function firstRun(form: RoutineForm, tz: string, now: Date, lead: string, withTime: boolean) {
  const checked = checkSchedule(scheduleInput(form), tz, now);
  if (!checked.ok) return "";
  const state = { enabled: true, paused_idle: false, ended_at: null };
  const next = nextRun({ ...checked.schedule, ...state }, tz, now)?.at;
  if (!next) return "";
  const date = localDate(next, tz);
  const day = nearDay(date, localDate(now, tz)) ?? longDate(date);
  return ` ${lead} ${day}${withTime ? `, ${clockAt(next, tz)}` : ""}`;
}

/** The routine_created / routine_started lines link to their routine (spec §11.4). */
const ROUTINE_LINES = new Set(["routine_created", "routine_changed", "routine_started"]);

/** A system line's routine, when it's one of the routine lines. */
export function lineRoutine(message: {
  kind: string;
  data: Record<string, unknown>;
}): string | null {
  if (message.kind !== "system" || !ROUTINE_LINES.has(String(message.data.event))) return null;
  const id = message.data.routine_id;
  return typeof id === "string" && id ? id : null;
}
